import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";
import * as XLSX from 'xlsx';

const POBTargetReport = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedQuarter, setSelectedQuarter] = useState("Q1");
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const printRef = useRef();

  const getQuarterDateRange = (quarter) => {
    const currentYear = new Date().getFullYear();
    let startDate, endDate;
    
    switch(quarter) {
      case 'Q1':
        startDate = `01/Apr/${currentYear}`;
        endDate = `30/Jun/${currentYear}`;
        break;
      case 'Q2':
        startDate = `01/Jul/${currentYear}`;
        endDate = `30/Sep/${currentYear}`;
        break;
      case 'Q3':
        startDate = `01/Oct/${currentYear}`;
        endDate = `31/Dec/${currentYear}`;
        break;
      case 'Q4':
        startDate = `01/Jan/${currentYear}`;
        endDate = `31/Mar/${currentYear}`;
        break;
      default:
        startDate = `01/Apr/${currentYear}`;
        endDate = `30/Jun/${currentYear}`;
    }
    
    return `${startDate}-${endDate}`;
  };

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders()
        });
        setStates(response.data);
      } catch (error) {
        console.error("Error fetching states:", error);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    const fetchDivisions = async () => {
      if (selectedState && selectedState !== "All") {
        try {
          const response = await axios.get(
            `${BASE_URL}/api/divisions?state_id=${selectedState}`,
            { headers: getAuthHeaders() }
          );
          setDivisions(response.data);
          setError(null);
        } catch (error) {
          console.error("Error fetching divisions:", error);
          setError("Failed to load divisions");
        }
      } else {
        setDivisions([]);
        setSelectedDivision("All");
      }
    };
    fetchDivisions();
  }, [selectedState]);

  const generateReport = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const quarterDateRange = getQuarterDateRange(selectedQuarter);
      
      const params = {
        quarter: quarterDateRange
      };
      
      if (selectedState && selectedState !== "All") {
        params.state_id = selectedState;
      }
      
      if (selectedDivision && selectedDivision !== "All") {
        const division = divisions.find(d => d.id == selectedDivision);
        params.division = division?.name;
      }

      const response = await axios.get(
        `${BASE_URL}/api/pob-targets/pob_target_report`,
        {
          params,
          headers: getAuthHeaders()
        }
      );
      
      setReportData(response.data.data || []);
    } catch (error) {
      console.error("Error fetching report:", error);
      setError("Failed to generate report");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return value ? parseFloat(value).toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }) : '0.00';
  };

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const handleExportExcel = () => {
    if (!reportData.length) return;

    const tableData = reportData.map((item, index) => ({
      "S.No": index + 1,
      "Empid": item.employee,
      "Employee": item.employee_name,
      "Division": item.division,
      "State": item.state,
      "HQ": item.hq,
      "Designation": item.designation,
      "DOJ": formatDate(item.final_joining_date),
      "DOR": formatDate(item.resign_date),
      "Phone": item.phone_no,
      "Target": item.target,
      "Running Total": item.running_total,
      "From Date": formatDate(item.start_date),
    }));

    const ws = XLSX.utils.json_to_sheet(tableData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "POB_Report");
    XLSX.writeFile(wb, "POB_Target_Report.xlsx");
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = reportData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(reportData.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="min-h-screen flex flex-col bg-white">

      <main className="flex-1 px-4 pt-6">
        <fieldset style={{ border: '1px solid #000', padding: '10px', marginBottom: '20px' }}>
          <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
            <span className="text-blue-700">POB Target </span>
            <span className="text-blue-500">Report</span>
          </h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center', justifyContent: 'center' }}>
            <div>
              <label>State</label><br />
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                style={{ width: '240px', padding: '6px', fontSize: '14px' }}
              >
                <option value="All">All States</option>
                {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label>Division</label><br />
              <select
                value={selectedDivision}
                onChange={(e) => setSelectedDivision(e.target.value)}
                style={{ width: '240px', padding: '6px', fontSize: '14px' }}
                disabled={!selectedState || selectedState === "All"}
              >
                <option value="All">All Divisions</option>
                {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label>Quarter</label><br />
              <select
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
                style={{ width: '240px', padding: '6px', fontSize: '14px' }}
              >
                <option value="Q1">Q1 (Apr - Jun)</option>
                <option value="Q2">Q2 (Jul - Sep)</option>
                <option value="Q3">Q3 (Oct - Dec)</option>
                <option value="Q4">Q4 (Jan - Mar)</option>
              </select>
            </div>
            <div>
              <CustomButton 
                onClick={generateReport} 
                label="Go" 
                variant="go" 
                disabled={isLoading}
              />
            </div>
          </div>
        </fieldset>

        {error && <div className="text-red-500 text-center mb-4">{error}</div>}
        {isLoading && <div className="text-center mb-4">Loading...</div>}

        {reportData.length > 0 && (
          <fieldset style={{ border: '1px solid #000', padding: '10px', marginBottom: '20px' }}>
            <div ref={printRef}>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
                <thead style={{ backgroundColor: '#ccf3ff' }}>
                  <tr>
                    {['S.No', 'Empid', 'Employee', 'Division', 'State', 'HQ', 'Designation', 'DOJ', 'DOR', 'Phone', 'Target', 'Pool', 'Running Total', 'From Date'].map((h, i) => (
                      <th key={i} style={{ border: '1px solid #000', padding: '4px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, i) => (
                    <tr key={i}>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{indexOfFirstItem + i + 1}</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{item.employee}</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{item.employee_name}</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{item.division}</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{item.state}</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{item.hq}</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{item.designation}</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{formatDate(item.final_joining_date)}</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{formatDate(item.resign_date)}</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{item.phone_no}</td>
                      <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'right' }}>{formatCurrency(item.target)}</td>
                      <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'right' }}>0</td>
                      <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'right' }}>{formatCurrency(item.running_total)}</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{formatDate(item.start_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-gray-100 border-t border-gray-300 mt-4">
                <div className="flex items-center mb-2 sm:mb-0">
                  <label htmlFor="itemsPerPage" className="mr-2 font-medium text-sm text-gray-700">View</label>
                  <select
                    id="itemsPerPage"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setCurrentPage(1);
                      setItemsPerPage(parseInt(e.target.value));
                    }}
                    className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
                  >
                    {[10, 20, 30, 50].map((count) => (
                      <option key={count} value={count}>{count}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600 font-medium">
                    {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, reportData.length)} of {reportData.length}
                  </span>
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    &larr;
                  </button>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages || reportData.length <= itemsPerPage}
                    className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    &rarr;
                  </button>
                </div>
              </div>
              <div className="pob-button-container mt-4 flex gap-4 justify-center">
                <CustomButton onClick={handlePrint} label="Print" variant="print" />
                <CustomButton onClick={handleExportExcel} label="Export" variant="export" />
              </div>
            </div>
          </fieldset>
        )}
      </main>
    </div>
  );
};

export default POBTargetReport;